/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import {
  Route,
  Redirect,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { isEmpty as _isEmpty, find as _find } from "lodash";
import { Auth, Amplify } from "aws-amplify";
import * as Sentry from "@sentry/react";
import { useSelector, useDispatch } from "react-redux";
import getMyTenants, { getMe } from "lib/requests/me";
import getConfigCat from "lib/requests/configCat";
import { REACT_APP_BASE_URL, REACT_APP_ENV } from "resources/constants";
import mixpanelTrack, { mixpanelBase } from "app/common/MixpanelComponent";
import { getCompanyNameFromUrl } from "lib/utils";
import awsConfig, { awsInternalConfig } from "aws-exports";
import Cookies from "universal-cookie";
import { useInterval, useEffectAsync } from "lib/react/customHooks";
import { browserName, browserVersion, osName } from "react-device-detect";
import { setTenant, setUser, setConfigCatUser } from "./duck/slices";
import TenantSelectorComponent from "./TenantSelectorComponent";

const cookies = new Cookies();

const PrivateRoute = ({ component: Component, ...rest }) => {
  const [dataIsReady, setDataIsReady] = useState(false);
  const [cognitoUser, setCognitoUser] = useState({});
  const [selectorState, setSelectorState] = useState({ show: false });
  const authSlice = useSelector((state) => state.auth);
  const history = useHistory();
  const dispatch = useDispatch();
  const { tenantId, section } = useParams();
  const { url } = useRouteMatch();

  useEffect(() => {
    const userEmail = cookies.get("userEmail")?.fieldsEmail;
    if (userEmail && userEmail.split("@")[1] === "wiseathena.com") {
      Amplify.configure(awsInternalConfig);
    } else {
      Amplify.configure(awsConfig);
    }
  }, []);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setCognitoUser(user);
        dispatch(setUser(user));
      })
      .finally(() => {
        setDataIsReady(true);
      });
  }, [Component]);

  const showTenantSelector = (tenants) => {
    history.replace("/");
    setSelectorState({ show: true, tenants });
  };

  const setConfigCatValues = async () => {
    const configCat = await getConfigCat();

    dispatch(
      setConfigCatUser({
        features: configCat,
      })
    );
  };

  useInterval(() => {
    setConfigCatValues();
  }, 60000);

  const getCurrentTenant = async (id) => {
    const tenants = (await getMyTenants()).data;

    const company = getCompanyNameFromUrl(window.location.href);
    if (company) {
      const tenant = _find(tenants, (t) => t.mysql_database === company);

      if (tenant) dispatch(setTenant(tenant));
      else
        history.push({
          pathname: "/logout",
          state: {
            message: "You are not authorized to access this data",
          },
        });

      return;
    }

    const tenant = _find(tenants, (t) => String(t.id) === String(id));

    if (tenant) {
      const me = await getMe();
      if (REACT_APP_ENV !== undefined) {
        Sentry.setUser({ email: me.email });
        Sentry.setTag("company", me.company?.name);
      }
      const baseBody = {
        data_instance: tenant.mysql_database,
        company: me.company?.name,
        service_type: "mixpanel",
        properties: {
          browser: browserName,
          browser_version: browserVersion,
          os: osName,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          current_url: REACT_APP_BASE_URL + url,
        },
      };
      mixpanelBase(baseBody);
      dispatch(setTenant(tenant));

      return;
    }
    // If the user only has access to one tenant, select it by default
    if (tenants.length === 1) {
      dispatch(setTenant(tenants[0]));
      return;
    }

    showTenantSelector(tenants);
  };

  useEffect(() => {
    if (!authSlice.tenant && tenantId) getCurrentTenant(tenantId);
    else showTenantSelector();
  }, []);

  useEffectAsync(
    async () => {
      const me = await getMe();
      return me;
    },
    (result) => {
      if (REACT_APP_ENV !== undefined) {
        Sentry.setUser({ email: result.email });
        Sentry.setTag("company", result.company?.name);
      }

      if (authSlice.tenant) {
        const baseBody = {
          data_instance: authSlice.tenant.mysql_database,
          company: result.company?.name,
          service_type: "mixpanel",
          properties: {
            browser: browserName,
            browser_version: browserVersion,
            os: osName,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            current_url: REACT_APP_BASE_URL + url,
          },
        };
        mixpanelBase(baseBody);
        mixpanelTrack("Log In");
        const sectionString = section ? `/${section}` : "";
        history.push(`/t/${authSlice.tenant.id}${sectionString}`);
        setConfigCatValues();
      }
    },
    [authSlice.tenant]
  );

  if (dataIsReady) {
    if (_isEmpty(cognitoUser)) return <Redirect to={{ pathname: "/login" }} />;
    return (
      <Route {...rest}>
        {() => {
          if (authSlice.tenant) return <Component />;
          if (selectorState.show)
            return <TenantSelectorComponent options={selectorState.tenants} />;
          return null;
        }}
      </Route>
    );
  }
  // else
  return null;
};

export default PrivateRoute;
